<template>
  <v-container fluid>
    <h1 class="text-h4 mb-4">Generate Enterprise Custom Subscription</h1>

    <v-row>
      <!-- Left Column: Quote Generation Form -->
      <v-col cols="12" md="6">
        <v-card class="sticky-form-card pa-4"> <!-- This is the main card for the sticky form column -->

          <div v-if="loadingUser" class="text-center pa-5">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
            <p class="mt-4">Loading user profile...</p>
          </div>

          <v-alert v-else-if="userError" type="error" prominent class="mb-4">
            Could not load user profile. Please ensure a valid user ID is provided.
          </v-alert>

          <template v-else-if="userProfile">
            <v-card-title class="text-h5">User Details</v-card-title>
            <v-card-text>
              <p><strong>Name:</strong> {{ userProfile.name }}</p>
              <p><strong>Organization:</strong> {{ userProfile.organization || 'N/A' }}</p>
              <p><strong>Email:</strong> {{ userProfile.email }}</p>
            </v-card-text>

            <v-form @submit.prevent="sendQuote" data-testid="quote-form">
              <v-row>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="form.monthlySubscriptionAmount"
                    label="Flat Monthly Amount"
                    type="number"
                    prefix="$"
                    :rules="monthlySubscriptionAmountRules"
                    required
                    data-testid="monthly-subscription-input"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="form.trueDepthUsageBaseCost"
                    label="Per-Scan Cost (Optional)"
                    type="number"
                    prefix="$"
                    :rules="trueDepthCostRules"
                    data-testid="truedepth-cost-input"
                  ></v-text-field>
                </v-col>
              </v-row>

              <v-alert v-if="quoteSentSuccessfully" type="success" prominent class="my-4">
                Terms sent successfully!
              </v-alert>
              <v-alert v-if="quoteError" type="error" prominent class="my-4">
                Error sending terms: {{ quoteError }}
              </v-alert>

              <v-btn
                type="submit"
                class="mt-4 str-btn-primary"
                :loading="sendingQuote"
                :disabled="sendingQuote || !userProfile || userError"
                data-testid="send-quote-button"
              >
                Send Custom Subscription
              </v-btn>
            </v-form>
          </template>

        </v-card> <!-- CLOSING TAG FOR THE MAIN STICKY CARD -->
      </v-col>

      <!-- Right Column: Quote Preview -->
      <v-col cols="12" md="6">
        <v-card class="quote-preview-card pa-4" ref="quotePreviewRef">
          <div class="Invoice">
            <div class="Invoice-Top-Row">
              <div id="Logo">
                <img :src="logoImage('StructureLogo-cyn-blk.svg')" alt="Structure Logo" style="width: 200px;">
              </div>
              <div class="InvoiceTitle">
                <span class="value">Custom Enterprise Subscription</span>
              </div>
            </div>

            <div class="header-contact-info">
                <div class="StoreAddress">
                    <div class="value">XRPro, LLC</div>
                    <div class="value">1600 Range Dr</div>
                    <div class="value">#101</div>
                    <div class="value">Boulder, CO 80303</div>
                </div>
            </div>

            <div class="info-grid">
                <div class="BillingAddress">
                    <div class="InvoiceHeading">Bill To</div>
                    <div class="AddressDetails">
                        <span class="value">{{ userProfile ? userProfile.organization : 'N/A' }}</span><br/>
                        <span class="value">{{ userProfile?.billing_address?.street_1 || '' }}</span><br v-if="userProfile?.billing_address?.street_1"/>
                        <span class="value">{{ userProfile?.billing_address?.street_2 || '' }}</span><br v-if="userProfile?.billing_address?.street_2"/>
                        <span class="value">{{ userProfile?.billing_address?.city || '' }}, {{ userProfile?.billing_address?.state || '' }} {{ userProfile?.billing_address?.postal_code || '' }}</span><br v-if="userProfile?.billing_address?.city"/>
                        <span class="value">{{ userProfile?.billing_address?.country || '' }}</span>
                    </div>
                </div>
            </div>
            <table class="InvoiceDataTable">
                <thead>
                    <tr>
                        <th>Date</th>
                        <th>Created by</th>
                        <th>Terms</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{{ quoteDate }}</td>
                        <td>{{ createdBy }}</td>
                        <td>
                            <ul>
                                <li>Payment will be remitted in USD.</li>
                                <li>Usage charges will be assessed at the end of each month.</li>
                            </ul>
                        </td>
                    </tr>
                </tbody>
            </table>
            <table class="OrderDeetsTable">
                <thead>
                    <tr>
                        <th>Product/Description</th>
                        <th>Amount</th>
                    </tr>
                </thead>
                <tbody class="InvoiceItemList">
                    <tr>
                        <td>Structure SDK Subscription - Enterprise</td>
                        <td>${{ formattedMonthlySubscriptionAmount }}</td>
                    </tr>
                    <tr>
                        <td>Structure SDK TrueDepth Usage per month</td>
                        <td>${{ formattedTrueDepthUsageBaseCost }} per scan</td>
                    </tr>
                </tbody>
            </table>
            <div class="closing-time">
                <div class="final-countdown">
                    <ol>
                        <li>Contact <a href="mailto:accounting@structure.io">accounting@structure.io</a> upon receipt if you disagree with any amounts, quantities or dates stated in this document.</li>
                        <li>Please notify us immediately if you are unable to pay on time.</li>
                    </ol>
                </div>
                <table class="OrderTotalsTable">
                    <tbody class="InvoiceTotals">
                        <tr>
                            <td><strong>Subscription Subtotal:</strong></td>
                            <td>${{ formattedMonthlySubscriptionAmount }}</td>
                        </tr>
                        <tr>
                            <td><strong>Usage Subtotal:</strong></td>
                            <td>${{ formattedTrueDepthUsageBaseCost }}</td>
                        </tr>
                        <tr>
                            <td><strong>Tax (0%):</strong></td>
                            <td>$0.00</td>
                        </tr>
                        <tr>
                            <td><strong>Total Due Monthly:</strong></td>
                            <td>${{ formattedMonthlySubscriptionAmount }} + usage</td>
                        </tr>
                    </tbody>
                </table>
            </div>
          </div>
        </v-card>
        <v-btn @click="downloadPdf" class="mt-4 str-btn-primary" data-testid="download-pdf-button">Download Subscription as PDF</v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-vue';
// import html2pdf from 'html2pdf.js'; // Will install this

const route = useRoute();
const { getAccessTokenSilently, user } = useAuth0();

const logoImage = (image) => require(`@/assets/images/logos/${image}`);


const userProfile = ref(null);
const loadingUser = ref(true);
const userError = ref(false);

const form = ref({
  monthlySubscriptionAmount: null,
  trueDepthUsageBaseCost: null,
});

const sendingQuote = ref(false);
const quoteSentSuccessfully = ref(false);
const quoteError = ref('');

const defaultRules = {
  required: value => !!value || 'Required.',
  positiveNumber: value => (value >= 0 && !isNaN(parseFloat(value))) || 'Must be a positive number.',
  optionalPositiveNumber: value => (value === null || value === '' || (value >= 0 && !isNaN(parseFloat(value)))) || 'Must be a positive number or empty.',
};

const monthlySubscriptionAmountRules = computed(() => {
  return [defaultRules.required, defaultRules.positiveNumber];
});

const trueDepthCostRules = computed(() => {
  return [defaultRules.optionalPositiveNumber];
});

// Computed properties for quote display
const quoteDate = computed(() => new Date().toLocaleDateString('en-US'));
const adminUserName = computed(() => user.value?.name || 'Admin User');
const createdBy = computed(() => adminUserName.value);

const formattedMonthlySubscriptionAmount = computed(() => {
  const amount = form.value.monthlySubscriptionAmount;
  return amount != null ? parseFloat(amount).toFixed(2) : '0.00';
});

const formattedTrueDepthUsageBaseCost = computed(() => {
  const amount = form.value.trueDepthUsageBaseCost;
  return amount != null ? parseFloat(amount).toFixed(2) : '0.00';
});

onMounted(async () => {
  const userId = route.params.id;
  if (userId) {
    try {
      loadingUser.value = true;
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(
        `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/users/${userId}`,
        { headers }
      );
      userProfile.value = response.data;
    } catch (e) {
      console.error('Error fetching user profile:', e);
      userError.value = true;
    } finally {
      loadingUser.value = false;
    }
  } else {
    userError.value = true;
    loadingUser.value = false; // <-- ADD THIS LINE
    console.error('No user ID provided in route parameters.');
  }
});

const quotePreviewRef = ref(null); // Ref for the quote preview element

const downloadPdf = async () => {
  if (quotePreviewRef.value) {
    const htmlContent = quotePreviewRef.value.$el.outerHTML;

    // Dynamically import html2pdf.js
    const html2pdf = (await import('html2pdf.js')).default;

    // Use a filename-friendly date (YYYY-MM-DD)
    const dateStr = new Date().toISOString().split('T')[0];
    const customerName = (userProfile.value?.organization || userProfile.value?.name || 'customer').replace(/[^\w\s-]/g, '').replace(/\s+/g, '-');

    const options = {
      margin: 10,
      filename: `${customerName}-terms-${dateStr}.pdf`,
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    html2pdf().set(options).from(htmlContent).save();
  }
};

const sendQuote = async () => {
  sendingQuote.value = true;
  quoteSentSuccessfully.value = false;
  quoteError.value = '';

  const userIdFromRoute = route.params.id;

  if (!userIdFromRoute) {
    quoteError.value = 'User ID is missing from URL parameters. Cannot send quote.';
    sendingQuote.value = false;
    return;
  }

  try {
    const token = await getAccessTokenSilently();
    const headers = { Authorization: `Bearer ${token}` };

    const dataToSubmit = { ...form.value };
    if (dataToSubmit.trueDepthUsageBaseCost === null || dataToSubmit.trueDepthUsageBaseCost === '') {
      dataToSubmit.trueDepthUsageBaseCost = 0.00;
    }

    const quoteDetails = {
      monthlySubscriptionAmount: dataToSubmit.monthlySubscriptionAmount,
      trueDepthUsageBaseCost: dataToSubmit.trueDepthUsageBaseCost,
      is_archived: false,
    };

    await axios.post(
      `${process.env.VUE_APP_CBE_BASE_URL}/piccolo/admin/quotes/send`,
      {
        user_auth0_id: userIdFromRoute,
        quote_details: quoteDetails,
      },
      { headers }
    );
    quoteSentSuccessfully.value = true;
  } catch (error) {
    console.error('Error sending quote:', error);
    quoteError.value = error.response?.data?.detail || error.message;
  } finally {
    sendingQuote.value = false;
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/StrVariables.scss";
@import "@/assets/styles/StrButtons.scss";
@import "@/assets/styles/QuoteGenerationView.scss";
</style>